import { createTheme } from '@mui/material/styles';

const canvas = '#0B0F19';
const card = '#111827';
const surface = '#182234';
const border = '#1F293D';
const hover = '#1E2C42';
const action = '#14B8A6';
const pressed = '#0D9488';
const onAction = '#042F2E';
// #042F2E sobre #0D9488 fica em ~3,9:1. #041C1C mantém o tom e passa de 4,5:1 no pressionado.
const onPressed = '#041C1C';
// O teal de ação é claro demais para texto branco. O item ativo do menu usa um teal mais fundo.
const navActive = '#0F766E';
const navHover = 'rgba(20, 184, 166, 0.16)';
const textPrimary = '#F8FAFC';
const textSecondary = '#94A3B8';
const success = '#10B981';
const warning = '#F59E0B';
const error = '#EF4444';

const headingFamily = '"Plus Jakarta Sans", Inter, Arial, sans-serif';
const bodyFamily = 'Inter, Arial, sans-serif';

const focusRing = {
  outline: `2px solid ${action}`,
  outlineOffset: 2,
};

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: action,
      dark: pressed,
      contrastText: onAction,
    },
    secondary: {
      main: textSecondary,
      contrastText: canvas,
    },
    background: {
      default: canvas,
      paper: card,
    },
    success: { main: success, contrastText: canvas },
    warning: { main: warning, contrastText: canvas },
    error: { main: error, contrastText: textPrimary },
    info: { main: textSecondary, contrastText: canvas },
    divider: border,
    text: {
      primary: textPrimary,
      secondary: textSecondary,
      disabled: textSecondary,
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
  typography: {
    fontFamily: bodyFamily,
    h1: { fontFamily: headingFamily, fontSize: '2rem', lineHeight: '40px', fontWeight: 700 },
    h2: { fontFamily: headingFamily, fontSize: '1.5rem', lineHeight: '32px', fontWeight: 600 },
    h3: { fontFamily: headingFamily, fontSize: '1.125rem', lineHeight: '26px', fontWeight: 600 },
    h4: { fontFamily: headingFamily, fontSize: '1.5rem', lineHeight: '32px', fontWeight: 600 },
    h5: { fontFamily: headingFamily, fontSize: '1.125rem', lineHeight: '26px', fontWeight: 600 },
    h6: { fontFamily: headingFamily, fontSize: '0.9375rem', lineHeight: '22px', fontWeight: 600 },
    subtitle1: { fontFamily: headingFamily, fontWeight: 600 },
    body1: { fontSize: '0.9375rem', lineHeight: '22px', fontWeight: 400 },
    body2: { fontSize: '0.875rem', lineHeight: '20px', fontWeight: 400 },
    button: { fontFamily: bodyFamily, fontWeight: 600, textTransform: 'none', fontSize: '0.875rem' },
    caption: { fontSize: '0.75rem', lineHeight: '16px' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: canvas,
          color: textPrimary,
        },
        '*:focus-visible': focusRing,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          minHeight: 40,
          paddingInline: 16,
          '&.Mui-focusVisible': focusRing,
          '&.Mui-disabled': {
            backgroundColor: surface,
            color: textSecondary,
            opacity: 1,
          },
        },
        containedPrimary: {
          backgroundColor: action,
          color: onAction,
          '&:hover': {
            backgroundColor: pressed,
            color: onPressed,
          },
          '&:active': {
            backgroundColor: pressed,
            color: onPressed,
          },
        },
        outlined: {
          borderColor: border,
          '&:hover': {
            borderColor: action,
            backgroundColor: hover,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': focusRing,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-focusVisible': focusRing,
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: card,
          borderRadius: 12,
          border: `1px solid ${border}`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          margin: 16,
          width: 'calc(100% - 32px)',
          maxHeight: 'calc(100% - 32px)',
          backgroundColor: card,
          backgroundImage: 'none',
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(11, 15, 25, 0.85)',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: card,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: border,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: hover,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: action,
            borderWidth: '1.5px',
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: error,
          },
          '&.Mui-disabled': {
            backgroundColor: surface,
          },
        },
        input: {
          fontFamily: bodyFamily,
          color: textPrimary,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: bodyFamily,
          '&.Mui-focused': { color: action },
          '&.Mui-error': { color: error },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          '&.Mui-error': { color: error },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: surface,
          color: textPrimary,
          fontFamily: bodyFamily,
          fontWeight: 600,
          borderBottom: `1px solid ${border}`,
        },
        body: {
          fontFamily: bodyFamily,
          color: textPrimary,
          borderBottom: `1px solid ${border}`,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: hover,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: bodyFamily,
          borderRadius: 999,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

theme.pontonow = {
  canvas,
  card,
  surface,
  border,
  hover,
  action,
  pressed,
  onAction,
  onPressed,
  navActive,
  navHover,
  textPrimary,
  textSecondary,
  success,
  warning,
  error,
  rowAlt: '#141D2D',
  sidebarWidth: 260,
  headerHeight: 64,
};

export default theme;
